import { useState, useEffect, useCallback } from "react";

const longPressMs: number = 200;

export enum MouseButton {
  LeftMouseButton = 0,
  RightMouseButton = 2,
};

export type ClickInfo = {
  button: MouseButton;
  x: number;
  y: number;
  pageX: number;
  pageY: number;
  longPress: boolean;
  clickTime: number | undefined;
};

type Props = {
  mouseButton: MouseButton; 
  enabled: boolean; 
  onClick: (info: ClickInfo) => void;
  onRelease: (info: ClickInfo) => void;
}

export const useClickHandler = (props: Props) => {
  const {
    mouseButton,
    enabled, 
    onClick,
    onRelease,
  } = props;

  const [x, setX] = useState<number>(0);
  const [y, setY] = useState<number>(0);
  const [pageX, setPageX] = useState<number>(0);
  const [pageY, setPageY] = useState<number>(0);
  const [isDown, setIsDown] = useState<boolean>(false);
  const [released, setReleased] = useState<boolean>(false);
  const [longPress, setLongPress] = useState<boolean>(false);
  const [checkLongPress, setCheckLongPress] = useState<boolean>(false);
  const [clickTime, setClickTime] = useState<number | undefined>(undefined);

  const handleClickHold = useCallback(() => {
    setCheckLongPress(true);
  }, []);

  const handleClickRelease = useCallback((e) => {
    if(e.button !== mouseButton) return;
    e.preventDefault();

    const clickInfo: ClickInfo = {
      button: mouseButton,
      x: x,
      y: y,
      pageX: pageX,
      pageY: pageY,
      clickTime: clickTime ? Date.now() - clickTime : undefined,
      longPress: longPress,
    };
    onRelease && onRelease(clickInfo);

    setReleased(true);
    setIsDown(false);
  }, [clickTime, longPress, mouseButton, onRelease, pageX, pageY, x, y]);

  const handleClickPress = useCallback(
    (e: MouseEvent) => {
      if(e.button !== mouseButton || !enabled) return;
      e.preventDefault();
      
      setIsDown(true);
      setClickTime(Date.now());

      setX(e.offsetX);
      setY(e.offsetY);
      setPageX(e.pageX);
      setPageY(e.pageY);

      setTimeout(handleClickHold, longPressMs);

      document.addEventListener("pointerup", handleClickRelease, {once: true});
    },
    [enabled, handleClickHold, handleClickRelease, mouseButton]
  );

  const handleContextMenu = useCallback(
    (e) => {
      e.preventDefault();
    },
    []
  );
  
  const finalizeClickEvent = useCallback((longPress: boolean) => {
    const clickInfo: ClickInfo = {
      button: mouseButton,
      x: x,
      y: y,
      pageX: pageX,
      pageY: pageY,
      clickTime: clickTime ? Date.now() - clickTime : undefined,
      longPress: longPress,
    };
    onClick(clickInfo);
  }, [clickTime, mouseButton, onClick, pageX, pageY, x, y]);

  useEffect(() => {
    if(released) {
      setReleased(false);

      if(enabled && !longPress) {
        // Fire normal click event
        finalizeClickEvent(false);
      }
      
      setLongPress(false);
      setClickTime(undefined);
    }
  }, [enabled, finalizeClickEvent, longPress, released]);
  
  useEffect(() => {
    if(checkLongPress) {
      if(isDown && enabled) {
        // Fire longpress click event
        finalizeClickEvent(true);
        setLongPress(true);
      }
      setCheckLongPress(false);
    }
  }, [checkLongPress, enabled, finalizeClickEvent, isDown]);

  useEffect(() => {
    document.addEventListener("pointerdown", handleClickPress);
    const isRightMB: boolean = mouseButton === MouseButton.RightMouseButton;
    isRightMB && document.addEventListener("contextmenu", handleContextMenu);
    return () => {
      document.removeEventListener("pointerdown", handleClickPress);
      isRightMB && document.removeEventListener("contextmenu", handleContextMenu);
    };
  });
};